import { useEffect, useRef } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Stack,
  Button,
  FormHelperText,
  TextField,
  FormControl,
  Typography,
} from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'
import { createNewsSchema } from './validations'
import { FileUpload } from 'components/FileUpload'
import { useCreateNewsMutation, useNewsItemQuery } from 'services/info-center/news'
import PageWrapper from 'hoc/PageWrapper'
import { useNavigate, useParams } from 'react-router-dom'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'

export type NewsFieldValues = {
  title: string
  content: string
  imageFileId?: string | undefined
}

export const CreateEditNews = () => {
  const { t } = useTranslation()
  const schema = createNewsSchema(t)
  const navigate = useNavigate()
  const { id } = useParams()
  const editorRef = useRef<ClassicEditor | null>(null)

  const [createNews, { isSuccess, isError }] = useCreateNewsMutation()
  const { data: newsItem } = useNewsItemQuery({ id: id ?? '' }, { skip: !id })

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    register,
  } = useForm<NewsFieldValues>({
    defaultValues: {
      title: newsItem ? newsItem.title : '',
      imageFileId: newsItem ? newsItem.imageFileId : '',
      content: newsItem ? newsItem.content : '',
    },
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (isSuccess) {
      toast.success(t('operation-success'))
    }
    if (isError) {
      toast.error(t('failedToCreate'))
    }
  }, [isSuccess, isError, t])

  const onSubmit = ({ title, imageFileId, content }: NewsFieldValues) => {
    if (id && newsItem) {
      createNews({
        id: newsItem?.id,
        title,
        imageFileId,
        content,
      }).then(() => navigate('/portal/news'))
    } else {
      createNews({
        id: uuidv4(),
        title,
        imageFileId,
        content,
      }).then(() => navigate('/portal/news'))
    }
  }

  const setDatatoEditor = (data: string) => {
    if (editorRef.current) {
      editorRef.current.setData(data)
    }
  }
  useEffect(() => {
    register('content')
  }, [])

  useEffect(() => {
    if (id && newsItem) {
      setDatatoEditor(newsItem?.content)
      setValue('title', newsItem?.title)
      setValue('imageFileId', newsItem?.imageFileId)
    }

    return () => {
      setDatatoEditor('<p></p>')
    }
  }, [id, newsItem])

  return (
    <PageWrapper>
      <Box display="flex" justifyContent="space-between">
        <Typography fontWeight="bold" variant="h6">
          {id ? t('edit-news') : t('create-news')}
        </Typography>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => navigate(`/portal/news`)}
        >
          {t('back')}
        </Button>
      </Box>
      <Stack sx={{ marginTop: '30px' }} rowGap={'20px'}>
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <Box>
              <TextField
                {...field}
                fullWidth
                required
                label={t('title')}
                size="small"
                variant="outlined"
              />
              {errors.title && (
                <FormHelperText error>{errors.title?.message}</FormHelperText>
              )}
            </Box>
          )}
        />
        <Box>
          <CKEditor
            editor={ClassicEditor}
            data={newsItem ? newsItem.content : '<p></p>'}
            config={{
              toolbar: [
                'heading',
                '|',
                'bold',
                'italic',
                'underline',
                '|',
                'link',
                'insertTable',
                '|',
                'undo',
                'redo',
              ],
            }}
            onReady={(editor) => (editorRef.current! = editor)}
            onChange={(value, editor) => {
              setValue('content', editor.getData())
            }}
          />
          {errors.content && (
            <FormHelperText error>{errors.content?.message}</FormHelperText>
          )}
        </Box>
        <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
          <FileUpload
            text={t('creationOrderFile')}
            handleFile={(file) => {
              setValue('imageFileId', file.id)
            }}
            onDelete={() => setValue('imageFileId', undefined)}
            fileId={newsItem?.imageFileId ?? undefined}
          />
        </FormControl>
      </Stack>
      <Stack
        sx={{ marrginTop: '20px' }}
        width="100%"
        direction="row"
        justifyContent="space-between"
      >
        <Button
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
          autoFocus
          onClick={handleSubmit(onSubmit)}
        >
          {t('create')}
        </Button>
      </Stack>
    </PageWrapper>
  )
}
